import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/index';
import LovedBy from '../LovedBy';

const RECAPTCHA = process.env.REACT_APP_RECAPTCHA;

// A few real AI-generated examples used in the showcase panel.
const exampleImages = [
  'https://firebasestorage.googleapis.com/v0/b/headshotsnowai-prod.firebasestorage.app/o/marketing%2F6ea74a4dc5049d90.jpg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/headshotsnowai-prod.firebasestorage.app/o/marketing%2F50ea4a071fcb6615.jpg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/headshotsnowai-prod.firebasestorage.app/o/marketing%2Fe8a661f14c55d16e.jpg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/headshotsnowai-prod.firebasestorage.app/o/marketing%2F4df30b3e3ce5188b.jpg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/headshotsnowai-prod.firebasestorage.app/o/marketing%2F60a297d735db38b1.jpg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/headshotsnowai-prod.firebasestorage.app/o/marketing%2F5c6d7b32fd46e486.jpg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/headshotsnowai-prod.firebasestorage.app/o/marketing%2Fd486ae76e71f1917.jpg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/headshotsnowai-prod.firebasestorage.app/o/marketing%2F495329578c427233.jpg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/headshotsnowai-prod.firebasestorage.app/o/marketing%2F0e9a26dc801859e6.jpg?alt=media',
];

function SignUpEmailAndGoogle({ onShowLogin, handleSubmitWithEmailLink, error, handleSubmitWithGoogle, isSubmitted, onSubmitted }) {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const popupRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const { t } = useTranslation();

  useEffect(() => {
    setCurrentLanguage(i18n.language || 'en');
  }, [i18n.language]);

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const onSubmitWithEmailLink = (e) => {
    e.preventDefault();
    if (loading) return;

    if (!email) {
      setEmailError(t('message.error.emptyEmail'));
      return;
    }
    if (!validateEmail(email)) {
      setEmailError(t('message.error.validEmail'));
      return;
    }

    setEmailError('');
    setLoading(true);
    handleSubmitWithEmailLink(e, t, currentLanguage, email);
  };

  const homeHref = currentLanguage === 'en' ? '/' : `/${currentLanguage}/`;

  return (
    <div className="grid min-h-screen w-full bg-black text-white lg:grid-cols-2">
      {/* Left: brand showcase (desktop only) */}
      <div className="relative hidden flex-col justify-between overflow-hidden p-12 lg:flex">
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-2">
          {exampleImages.slice(0, 9).map((url, i) => (
            <img key={i} src={url} alt="" loading="lazy" className="h-full w-full rounded-lg object-cover" />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/50" />

        <div className="relative z-10">
          <Link to={homeHref} aria-label="HeadshotsNow.ai">
            <img src="/svg/headshotsnow_logo.webp" width="210" height="53" alt="HeadshotsNow.ai" />
          </Link>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-3xl font-bold leading-tight">
            Your professional headshots,{' '}
            <span className="text-brand-light">delivered online</span>
          </h1>
          <ul className="mt-6 space-y-3 text-white/80">
            <li className="flex items-center gap-3"><span className="text-brand-light">✓</span> AI-generated in about an hour</li>
            <li className="flex items-center gap-3"><span className="text-brand-light">✓</span> Dozens of styles for LinkedIn, resumes &amp; bios</li>
            <li className="flex items-center gap-3"><span className="text-brand-light">✓</span> Private — you own every headshot</li>
          </ul>
          <div className="mt-8">
            <LovedBy t={t} />
          </div>
        </div>
      </div>

      {/* Right: auth card */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div ref={popupRef} className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="mb-8 flex justify-center lg:hidden">
            <Link to={homeHref} aria-label="HeadshotsNow.ai">
              <img src="/svg/headshotsnow_logo.webp" width="200" height="50" alt="HeadshotsNow.ai" />
            </Link>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 shadow-xl sm:p-8">
            <h2 className="text-center text-xl font-semibold text-white">{t('signup.title')}</h2>

            {/* Google */}
            <button
              onClick={(e) => handleSubmitWithGoogle(e, t)}
              className="mt-6 flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white/5 py-3.5 font-semibold text-white transition duration-200 hover:bg-white/10"
            >
              <img src="fonts/6373e8bb67056287b1380aa8_image%206.svg" loading="lazy" alt="" className="h-5 w-5" />
              {t('button.googleSignup')}
            </button>

            {/* Divider */}
            <div className="my-5 flex items-center gap-3 text-sm text-white/40">
              <div className="h-px flex-1 bg-white/10" />
              {t('signin.another')}
              <div className="h-px flex-1 bg-white/10" />
            </div>

            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-2 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 border border-brand/30">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-brand-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-white">{t('giveAway.emailSentTitle')} 🎉</h3>
                <p className="mt-1 text-sm text-white/60">{t('giveAway.emailSentSub')}</p>
              </div>
            ) : (
              <>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('giveAway.enterYourEmail')}
                  className="w-full rounded-lg border border-white/15 bg-white/[0.04] px-4 py-3.5 text-white placeholder-white/40 transition focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
                {emailError && <p className="mt-2 text-sm text-red-400">{emailError}</p>}

                <button
                  onClick={onSubmitWithEmailLink}
                  disabled={loading}
                  className="mt-3 w-full rounded-full bg-brand py-3.5 font-semibold text-white shadow-lg shadow-brand/20 transition duration-200 hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? t('giveAway.submitting') : t('button.emailSignup')}
                </button>

                <p className="mt-5 text-center text-xs leading-relaxed text-white/45">
                  {t('tos.agree')}
                  <a
                    className="text-white/70 underline hover:text-white"
                    href={currentLanguage === 'en' ? '/legal/?view=terms' : `/${currentLanguage}/legal/?view=terms`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('footer.terms')}
                  </a>
                  {' '}&amp;{' '}
                  <a
                    className="text-white/70 underline hover:text-white"
                    href={currentLanguage === 'en' ? '/legal/?view=privacy-policy' : `/${currentLanguage}/legal/?view=privacy-policy`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('footer.privacy')}
                  </a>
                </p>
              </>
            )}
          </div>

          {/* Mobile social proof */}
          <div className="mt-6 flex justify-center lg:hidden">
            <LovedBy t={t} />
          </div>
        </div>
      </div>
    </div>
  );
}

SignUpEmailAndGoogle.propTypes = {
  handleChange: PropTypes.func,
  error: PropTypes.object,
};

export default SignUpEmailAndGoogle;
